import { defineEndpoint, PageQuery, SearchQuery, SearchResult } from '@itc/shared';
import { z } from 'zod';
import { Loan, LoanInput, LoanUpdate, RepayInput } from './loans.ts';
import { Account, AccountInput, AccountUpdate, Entry, EntryActions, EntryInput, EntryKind, EntryStatus, GroupLimits, GroupLimitsInput, Limits, People, Period, Person, RecordId, Summary } from './schemas.ts';
import { ClosePeriodInput, ClosePreview, Mine, NeedsMe, ReconcileNowInput, Reconciliation, ReportInput } from './reconcile.ts';

// The bank's contracts (design §7.3, plan 2A). Every change takes an
// Idempotency-Key, so a retried request never records money twice; account changes
// also need a recent login.

const ById = z.object({ id: RecordId });

export const getSummary = defineEndpoint({
  method: 'GET',
  path: '/bank/summary',
  permission: 'bank.view',
  response: Summary,
});

/** Every account, inactive ones included, for the admin section. */
export const listAccounts = defineEndpoint({
  method: 'GET',
  path: '/bank/accounts',
  permission: 'bank.admin',
  response: z.object({ accounts: z.array(Account), people: People }),
});

export const createAccount = defineEndpoint({
  method: 'POST',
  path: '/bank/accounts',
  permission: 'bank.admin',
  recentLogin: true,
  idempotencyKey: 'required',
  body: AccountInput,
  response: Account,
});

export const updateAccount = defineEndpoint({
  method: 'PUT',
  path: '/bank/accounts/:id',
  permission: 'bank.admin',
  recentLogin: true,
  idempotencyKey: 'required',
  params: ById,
  body: AccountUpdate,
  response: Account,
});

/** Only at a zero balance, with nothing pending against it. */
export const deactivateAccount = defineEndpoint({
  method: 'POST',
  path: '/bank/accounts/:id/deactivate',
  permission: 'bank.admin',
  recentLogin: true,
  idempotencyKey: 'required',
  params: ById,
  response: Account,
});

export const listPeriods = defineEndpoint({
  method: 'GET',
  path: '/bank/periods',
  permission: 'bank.view_ledger',
  /** Newest first; the first is the current period. */
  response: z.object({ periods: z.array(Period) }),
});

/** The ledger, newest first. Without `period`, the current period's. */
export const listEntries = defineEndpoint({
  method: 'GET',
  path: '/bank/entries',
  permission: 'bank.view_ledger',
  query: PageQuery.extend({
    kind: EntryKind.optional(),
    status: EntryStatus.optional(),
    /** Entries into or out of this account. */
    account: RecordId.optional(),
    period: z.coerce.number().int().min(1).optional(),
  }),
  response: z.object({ items: z.array(Entry), nextCursor: z.string().nullable(), people: People }),
});

/** One entry: for ledger viewers, and for its recorder, its payee and the holders of the accounts it moves (D16). */
/** R15: what the caller may find. Open to any login, as each kind has its own permission, checked inside. */
export const searchBank = defineEndpoint({ method: 'GET', path: '/bank/search', permission: 'authenticated', query: SearchQuery, response: SearchResult });

/** For a thank-you image (plan 2E R11): what the entry's donor has given, for whoever may read the entry. */
export const getDonorTotals = defineEndpoint({ method: 'GET', path: '/bank/entries/:id/donor-totals', permission: 'authenticated', params: ById, response: z.object({ total: z.number().int(), count: z.number().int() }) });

export const getEntry = defineEndpoint({
  method: 'GET',
  path: '/bank/entries/:id',
  permission: 'authenticated',
  params: ById,
  /** `accounts`: the names of the accounts it moves, by id, for readers who can't see the summary. */
  response: z.object({ entry: Entry, people: People, actions: EntryActions, accounts: z.record(z.string(), z.string()) }),
});

/** What's waiting on the caller: their own waiting entries, transfers to confirm, and payouts to them. */
export const getMine = defineEndpoint({
  method: 'GET',
  path: '/bank/mine',
  permission: 'authenticated',
  response: Mine,
});

/** Active members matching `q`, for holder, payee and donor pickers: bank.record or bank.admin (checked in the handler). */
export const searchPeople = defineEndpoint({
  method: 'GET',
  path: '/bank/people',
  permission: 'authenticated',
  query: z.object({ q: z.string().trim().max(64).default('') }),
  response: z.object({ people: z.array(Person) }),
});

/** Records an entry. It completes at once within the recorder's limits; otherwise it waits for an approver. */
export const recordEntry = defineEndpoint({
  method: 'POST',
  path: '/bank/entries',
  permission: 'bank.record',
  idempotencyKey: 'required',
  body: EntryInput,
  response: z.object({ entry: Entry, people: People }),
});

const EntryResult = z.object({ entry: Entry, people: People });
const Note = z.object({ note: z.string().trim().max(500).default('') });

/** Completes a pending entry: an approver whose limit covers it, never its recorder. */
export const approveEntry = defineEndpoint({
  method: 'POST',
  path: '/bank/entries/:id/approve',
  permission: 'bank.approve',
  idempotencyKey: 'required',
  params: ById,
  response: EntryResult,
});

/** Refuses a pending entry or a transfer waiting for receipt; the note goes to its recorder. */
export const rejectEntry = defineEndpoint({
  method: 'POST',
  path: '/bank/entries/:id/reject',
  permission: 'bank.approve',
  idempotencyKey: 'required',
  params: ById,
  body: Note,
  response: EntryResult,
});

/** Withdraws an entry its recorder no longer wants, while it's still waiting. */
export const cancelEntry = defineEndpoint({
  method: 'POST',
  path: '/bank/entries/:id/cancel',
  permission: 'bank.record',
  idempotencyKey: 'required',
  params: ById,
  response: EntryResult,
});

/**
 * Confirms money arrived: a transfer, by a holder of the receiving account (which
 * completes it), or a payout, by its payee. Open to any logged-in user; the handler
 * checks they're the one.
 */
export const confirmEntry = defineEndpoint({
  method: 'POST',
  path: '/bank/entries/:id/confirm',
  permission: 'authenticated',
  idempotencyKey: 'required',
  params: ById,
  response: EntryResult,
});

/** A payee says a payout didn't arrive. No money moves; approvers are told. */
export const disputeEntry = defineEndpoint({
  method: 'POST',
  path: '/bank/entries/:id/dispute',
  permission: 'authenticated',
  idempotencyKey: 'required',
  params: ById,
  body: z.object({ note: z.string().trim().min(1).max(1000) }),
  response: EntryResult,
});

/** Records the entry that undoes a completed one, under the same rules as any entry. */
export const reverseEntry = defineEndpoint({
  method: 'POST',
  path: '/bank/entries/:id/reverse',
  permission: 'bank.record',
  idempotencyKey: 'required',
  params: ById,
  body: z.object({ memo: z.string().trim().max(500).default('') }),
  response: EntryResult,
});

/** The caller's own limits, so pages can say what will need approval. */
export const getMyLimits = defineEndpoint({
  method: 'GET',
  path: '/bank/limits/mine',
  permission: 'authenticated',
  response: Limits,
});

/** Every group, with its limits (zero until set). */
export const listGroupLimits = defineEndpoint({
  method: 'GET',
  path: '/bank/limits',
  permission: 'bank.admin',
  response: z.object({ groups: z.array(GroupLimits) }),
});

export const setGroupLimits = defineEndpoint({
  method: 'PUT',
  path: '/bank/limits/:group',
  permission: 'bank.admin',
  recentLogin: true,
  idempotencyKey: 'required',
  params: z.object({ group: RecordId }),
  body: GroupLimitsInput,
  response: GroupLimits,
});

// Loans (plan 2A Chunk 4). Visible to ledger viewers; lending and repaying are
// ledger entries, so they follow the same limits as any payout or deposit.

const LoanResult = z.object({ loan: Loan, people: People });
const LoanWithEntry = z.object({ loan: Loan, entry: Entry.nullable(), people: People });

export const listLoans = defineEndpoint({
  method: 'GET',
  path: '/bank/loans',
  permission: 'bank.view_ledger',
  response: z.object({ loans: z.array(Loan), people: People }),
});

/** Creates a loan; with `lendNow`, its lending payout too, in the same transaction. */
export const createLoan = defineEndpoint({
  method: 'POST',
  path: '/bank/loans',
  permission: 'bank.record',
  idempotencyKey: 'required',
  body: LoanInput,
  response: LoanWithEntry,
});

export const updateLoan = defineEndpoint({
  method: 'PUT',
  path: '/bank/loans/:id',
  permission: 'bank.record',
  idempotencyKey: 'required',
  params: ById,
  body: LoanUpdate,
  response: LoanResult,
});

export const lendLoan = defineEndpoint({
  method: 'POST',
  path: '/bank/loans/:id/lend',
  permission: 'bank.record',
  idempotencyKey: 'required',
  params: ById,
  response: LoanWithEntry,
});

export const repayLoan = defineEndpoint({
  method: 'POST',
  path: '/bank/loans/:id/repay',
  permission: 'bank.record',
  idempotencyKey: 'required',
  params: ById,
  body: RepayInput,
  response: LoanWithEntry,
});

/** Writes a loan off; no money moves. Reconcilers hold bank.approve through their suggested group. */
export const writeOffLoan = defineEndpoint({
  method: 'POST',
  path: '/bank/loans/:id/write-off',
  permission: 'bank.approve',
  idempotencyKey: 'required',
  params: ById,
  response: LoanResult,
});

/** Only a loan none of whose entries completed. */
export const deleteLoan = defineEndpoint({
  method: 'DELETE',
  path: '/bank/loans/:id',
  permission: 'bank.record',
  idempotencyKey: 'required',
  params: ById,
  response: z.object({ ok: z.literal(true) }),
});

// Reconciliation and period close (plan 2A Chunk 5). Holders report through
// bank.view, which members hold; the handler checks they hold the account.

export const listReconciliations = defineEndpoint({
  method: 'GET',
  path: '/bank/reconciliations',
  permission: 'bank.view',
  /** The latest month's: this month's once opened. */
  response: z.object({ month: z.string().nullable(), items: z.array(Reconciliation) }),
});

const ReconciliationResult = z.object({ reconciliation: Reconciliation, entry: Entry.nullable(), people: People });

/** A holder reports the in-game balance; a difference becomes an adjustment. */
export const reportReconciliation = defineEndpoint({
  method: 'POST',
  path: '/bank/reconciliations/:id/report',
  permission: 'bank.view',
  idempotencyKey: 'required',
  params: ById,
  body: ReportInput,
  response: ReconciliationResult,
});

/** A holder confirms the balance is unchanged, in one click. */
export const confirmUnchanged = defineEndpoint({
  method: 'POST',
  path: '/bank/reconciliations/:id/unchanged',
  permission: 'bank.view',
  idempotencyKey: 'required',
  params: ById,
  response: z.object({ reconciliation: Reconciliation }),
});

/** A reconciler reconciles an account at any time: an adjustment that completes at once. */
export const reconcileAccount = defineEndpoint({
  method: 'POST',
  path: '/bank/accounts/:id/reconcile',
  permission: 'bank.reconcile',
  idempotencyKey: 'required',
  params: ById,
  body: ReconcileNowInput,
  response: z.object({ entry: Entry.nullable(), people: People, reconciliation: Reconciliation.nullable() }),
});

export const previewClose = defineEndpoint({
  method: 'GET',
  path: '/bank/periods/close',
  permission: 'bank.reconcile',
  response: ClosePreview,
});

/** Closes the period for a wipe: every balance to zero, and the next period starts. */
export const closePeriod = defineEndpoint({
  method: 'POST',
  path: '/bank/periods/close',
  permission: 'bank.reconcile',
  recentLogin: true,
  idempotencyKey: 'required',
  body: ClosePeriodInput,
  response: z.object({ closed: Period, current: Period }),
});

/** What's waiting on the caller, counted for the Bank nav badge. Refetched on bank.me and, for approvers, bank.approvals. */
export const getNeedsMe = defineEndpoint({
  method: 'GET',
  path: '/bank/needs-me',
  permission: 'authenticated',
  response: NeedsMe,
});
